/** 经营总览：雷达图与订单列表分页常量 */

import { formatContractTotal } from './overviewMetricsFormat';

/** 总览第四行订单列表缺省页大小回退 */
/** 对齐 Falcon RecentPurchases perPage: 7 */
export const OVERVIEW_ORDER_LIST_PAGE_SIZE = 7;

const OVERVIEW_RADAR_AXES = [
	{ key: 'contract', label: '合同金额' },
	{ key: 'count', label: '订单量' },
	{ key: 'profitRate', label: '利润率' },
	{ key: 'overdue', label: '超期' },
	{ key: 'completionRate', label: '完成率' },
	{ key: 'cycleDays', label: '订单周期' },
];

const resolveOverviewCompletionRate = (stats, overview = {}) => {
	const overviewCount = Number(overview.count) || 0;
	const saleRows = stats?.chartAggregates?.dimensions?.saleUser;
	if (Array.isArray(saleRows) && saleRows.length > 0) {
		const totals = saleRows.reduce(
			(acc, row) => {
				acc.count += Number(row.count) || 0;
				acc.completed += Number(row.completedCount) || 0;
				return acc;
			},
			{ count: 0, completed: 0 },
		);
		if (totals.count > 0) return (totals.completed / totals.count) * 100;
	}
	const completedPhase = (stats?.lenses?.operational?.phases ?? stats?.phases ?? []).find((row) => row.phase === 'completed');
	const completedCount = Number(completedPhase?.count) || 0;
	if (overviewCount > 0) return (completedCount / overviewCount) * 100;
	return 0;
};

const buildRadarRawSnapshot = ({ overview = {}, overdueTotal = 0, completionRate = 0, cycleDays = 0, showFinancial = true }) => {
	const price = Number(overview.totalPrice) || 0;
	const profit = Number(overview.trueProfitTotal) || 0;
	return {
		contract: price,
		count: Number(overview.count) || 0,
		profitRate: showFinancial && price > 0 ? (profit / price) * 100 : 0,
		overdue: Math.max(0, Number(overdueTotal) || 0),
		completionRate: Math.max(0, Number(completionRate) || 0),
		cycleDays: Math.max(0, Number(cycleDays) || 0),
	};
};

const normalizeRadarValues = (raw, maxByKey) =>
	Object.fromEntries(OVERVIEW_RADAR_AXES.map((axis) => [axis.key, Math.max(0, Number(raw[axis.key]) || 0) / Math.max(1, maxByKey[axis.key])]));

const formatRadarDeltaPercent = (current, previous) => {
	const curr = Number(current) || 0;
	const prev = Number(previous) || 0;
	if (prev === 0) return curr > 0 ? { label: '—', up: true } : null;
	const pct = ((curr - prev) / Math.abs(prev)) * 100;
	return {
		label: `${Math.abs(pct).toFixed(0)}%`,
		up: pct >= 0,
	};
};

/**
 * 经营雷达：六轴归一化到 0–1，图例金额取合同额；同比有则双系列。
 * 订单周期暂无服务端聚合时为 0（不伪造）。
 */

/**
 * 经营雷达：六轴归一化到 0–1，图例金额取合同额；同比有则双系列。
 * 订单周期暂无服务端聚合时为 0（不伪造）。
 */
export const buildOverviewRadarBundle = ({
	overview = {},
	yoyOverview = null,
	overdueTotal = 0,
	yoyOverdueTotal = 0,
	stats = null,
	showFinancial = true,
	currentName = '本期',
	previousName = '同比',
} = {}) => {
	const currentRaw = buildRadarRawSnapshot({
		overview,
		overdueTotal,
		completionRate: resolveOverviewCompletionRate(stats, overview),
		cycleDays: 0,
		showFinancial,
	});
	const previousRaw = yoyOverview
		? buildRadarRawSnapshot({
				overview: yoyOverview,
				overdueTotal: yoyOverdueTotal,
				completionRate: resolveOverviewCompletionRate({ chartAggregates: null, lenses: null, phases: yoyOverview.phases }, yoyOverview),
				cycleDays: 0,
				showFinancial,
			})
		: null;

	const maxByKey = Object.fromEntries(
		OVERVIEW_RADAR_AXES.map((axis) => {
			const curr = Number(currentRaw[axis.key]) || 0;
			const prev = Number(previousRaw?.[axis.key]) || 0;
			const ceiling = Math.max(curr, prev, axis.key === 'profitRate' || axis.key === 'completionRate' ? 100 : 1);
			return [axis.key, ceiling];
		}),
	);

	const indicators = OVERVIEW_RADAR_AXES.map((axis) => ({
		key: axis.key,
		label: axis.label,
		max: 1,
	}));

	const currentDelta = previousRaw ? formatRadarDeltaPercent(currentRaw.contract, previousRaw.contract) : null;

	return {
		indicators,
		current: {
			name: currentName,
			values: normalizeRadarValues(currentRaw, maxByKey),
			raw: currentRaw,
			summaryValue: currentRaw.contract,
			summaryLabel: formatContractTotal(currentRaw.contract),
			deltaLabel: currentDelta?.label,
			deltaUp: currentDelta?.up,
		},
		previous: previousRaw
			? {
					name: previousName,
					values: normalizeRadarValues(previousRaw, maxByKey),
					raw: previousRaw,
					summaryValue: previousRaw.contract,
					summaryLabel: formatContractTotal(previousRaw.contract),
					deltaLabel: null,
					deltaUp: undefined,
				}
			: null,
	};
};
